import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import cn from "classnames";
import dayjs from "dayjs";
import Page from "../Page";
import CustomSelect from "../CustomSelect";
import BasicButton from "../../BasicButton";
import { ReactComponent as ChevronIcon } from "../Sidebar/assets/arrow-left.svg";
import { GET_ADMIN_LOG, GET_ADMIN_LOG_ADMINS } from "../../api/graphql/logs";
import styles from "./styles.module.scss";

/**
 * The admin log: which admin changed what — in the panel, and inside a
 * user's account while signed in as them. The platform's Activity feed,
 * turned on the admins.
 */

/** One value the action set: "Status" — "DONE". */
type Change = { subject: string; detail: string };

type Entry = {
  id: string;
  adminId: number;
  adminEmail: string;
  entityType: string;
  action: string;
  entityId: string | null;
  userId: number | null;
  userEmail: string | null;
  sessionId: string | null;
  operation: string;
  changes: Change[];
  success: boolean;
  error: string | null;
  ip: string | null;
  createdAt: string;
};

type Admin = { id: number; email: string };

const PAGE = 50;
const ALL = "all";

const FILTERS: { value: string; label: string }[] = [
  { value: ALL, label: "All" },
  { value: "user", label: "Users" },
  { value: "order", label: "Orders" },
  { value: "moderation", label: "Moderation" },
  { value: "pricing", label: "Prices" },
  { value: "billing", label: "Billing" },
  { value: "admins", label: "Admins" },
  { value: "reports", label: "IDFA reports" },
  { value: "settings", label: "Settings" },
  { value: "session", label: "Sign-ins" },
  { value: "as_user", label: "As user" },
  { value: "other", label: "Other" },
];

const KIND_LABEL: Record<string, string> = {
  user: "user",
  order: "order",
  moderation: "moderation",
  pricing: "prices",
  billing: "billing",
  admins: "admins",
  reports: "idfa",
  settings: "settings",
  session: "sign-in",
  as_user: "as user",
  other: "other",
};

/** A line's shade: the sidebar tone of the section it belongs to. */
const TONE: Record<string, string> = {
  user: styles.toneUser,
  order: styles.toneOrder,
  moderation: styles.toneModeration,
  pricing: styles.tonePricing,
  billing: styles.toneBilling,
  reports: styles.toneReports,
  as_user: styles.toneAsUser,
};

const when = (iso: string): string => {
  const stamp = dayjs(iso);
  const minutes = Math.max(0, dayjs().diff(stamp, "minute"));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return stamp.format("DD.MM.YYYY HH:mm");
};

/**
 * A log line. The sentence says what happened; opening it shows what was
 * set, and where the request came from.
 */
const Row: React.FC<{ entry: Entry }> = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const details: Change[] = [
    ...entry.changes,
    ...(entry.error ? [{ subject: "Error", detail: entry.error }] : []),
    ...(entry.ip ? [{ subject: "IP", detail: entry.ip }] : []),
    { subject: "Operation", detail: entry.operation },
  ];

  return (
    <div className={cn(styles.row, { [styles.rowFailed]: !entry.success })}>
      {/* The whole line toggles; the chevron is its keyboard handle, and
          its click reaches here like any other. */}
      <div className={styles.rowHead} onClick={() => setOpen(!open)}>
        <span className={cn(styles.kind, TONE[entry.entityType])}>
          {KIND_LABEL[entry.entityType] ?? entry.entityType}
        </span>
        <div className={styles.rowText}>
          <span className={styles.action}>
            {entry.action}
            {!entry.success && <span className={styles.failed}>Failed</span>}
          </span>
          <span className={styles.meta}>
            <span>{entry.adminEmail}</span>
            {entry.userId != null && (
              <>
                {" · "}
                <Link
                  to={`/user/${entry.userId}`}
                  className={styles.userLink}
                  onClick={(event) => event.stopPropagation()}
                >
                  {entry.userEmail ?? `user #${entry.userId}`}
                </Link>
              </>
            )}
            {" · "}
            <time
              dateTime={entry.createdAt}
              title={dayjs(entry.createdAt).format("DD.MM.YYYY HH:mm:ss")}
            >
              {when(entry.createdAt)}
            </time>
          </span>
        </div>
        <button
          type="button"
          className={cn(styles.rowToggle, { [styles.rowToggleOpen]: open })}
          aria-expanded={open}
          aria-label={open ? "Collapse details" : "Expand details"}
        >
          <ChevronIcon />
        </button>
      </div>

      {open && (
        <div className={styles.changes}>
          {details.map((change, i) => (
            // A subject can repeat (two keywords of one order), so it is not
            // a key on its own.
            <div className={styles.change} key={`${change.subject}-${i}`}>
              <span className={styles.changeSubject}>{change.subject}</span>
              <span
                className={cn(styles.changeDetail, {
                  [styles.changeError]: change.subject === "Error",
                })}
                title={change.detail}
              >
                {change.detail}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminLogs: React.FC = () => {
  const [filter, setFilter] = useState(ALL);
  const [adminId, setAdminId] = useState<number | null>(null);
  const [limit, setLimit] = useState(PAGE);

  // Both filters go to the backend, so paging holds for any combination of
  // them rather than only for the page already on screen.
  const { data, loading, error } = useQuery(GET_ADMIN_LOG, {
    variables: { limit, offset: 0, entityType: filter, adminId },
    fetchPolicy: "cache-and-network",
  });
  const { data: adminsData } = useQuery(GET_ADMIN_LOG_ADMINS, {
    fetchPolicy: "cache-and-network",
  });

  const entries: Entry[] = data?.adminLog ?? [];
  const admins: Admin[] = adminsData?.adminLogAdmins ?? [];

  /** Changing a filter starts the feed over: the old page count means nothing now. */
  const narrow = (change: () => void) => {
    setLimit(PAGE);
    change();
  };

  // The feed asks for one page more each time, so "Load more" is worth
  // offering only while the last answer filled the page it asked for.
  const hasMore = entries.length >= limit;

  return (
    <Page fitScreen className={styles.page}>
      <div className={styles.filters}>
        {FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={cn(styles.filter, {
              [styles.filterActive]: filter === option.value,
            })}
            onClick={() => narrow(() => setFilter(option.value))}
          >
            {option.label}
          </button>
        ))}

        {/* Only worth offering once more than one admin is in the log. */}
        {admins.length > 1 && (
          <CustomSelect
            className={styles.adminSelect}
            aria-label="Admin"
            value={adminId == null ? ALL : String(adminId)}
            onChange={(value) =>
              narrow(() => setAdminId(value === ALL ? null : Number(value)))
            }
            options={[
              { value: ALL, label: "All admins" },
              ...admins.map((admin) => ({
                value: String(admin.id),
                label: admin.email,
              })),
            ]}
          />
        )}
      </div>

      <div className={styles.body}>
        {error && !data ? (
          <p className={styles.empty}>The log could not be loaded.</p>
        ) : loading && !data ? (
          <p className={styles.empty}>Loading…</p>
        ) : entries.length === 0 ? (
          <p className={styles.empty}>
            {filter === ALL && adminId == null
              ? "No admin has changed anything yet."
              : "Nothing matches these filters."}
          </p>
        ) : (
          <div className={styles.feed}>
            {entries.map((entry) => (
              <Row key={entry.id} entry={entry} />
            ))}
          </div>
        )}

        {hasMore && (
          <div className={styles.more}>
            <BasicButton
              variant="secondary"
              title={loading ? "Loading…" : "Load more"}
              onClick={() => setLimit(limit + PAGE)}
              disabled={loading}
            />
          </div>
        )}
      </div>
    </Page>
  );
};

export default AdminLogs;
